import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { useMDTeamLoad } from '../hooks/useMDTeamLoad';
import type { StatisticPeriod, MDTeamLoadLawyer } from '../api/dashboard.api';

// K1 (0–5 quality index): mockup shows ≥4 healthy (green), below 4 attention (crimson).
function k1Class(k1: number | null): string {
  if (k1 == null) return 'text-slate-400';
  return k1 >= 4 ? 'text-emerald-600' : 'text-red-600';
}

// Utilization (billable ÷ capacity): kept muted like the mockup — only the
// extremes are colored so the common 50–80% band reads as neutral.
function utilClass(util: number): string {
  if (util >= 80) return 'text-emerald-600';
  if (util < 50) return 'text-red-600';
  return 'text-slate-700';
}

function TeamLoadRow({ lawyer }: { lawyer: MDTeamLoadLawyer }) {
  const { t } = useTranslation('common');
  return (
    <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70">
      <td className="py-2.5 pr-3">
        <Link
          to={`/app/users/${lawyer.id}`}
          className="font-medium text-slate-900 hover:text-[#0069D1] hover:underline"
        >
          {lawyer.name ?? '—'}
        </Link>
      </td>
      <td className="py-2.5 pr-3 text-slate-500">{lawyer.roleName ?? '—'}</td>
      <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">{lawyer.tasks}</td>
      <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">{lawyer.cases}</td>
      {/* K1 = reviewer QA score. K3 = manager rating. Two different components
          of the §10.1 formula — this column used to show the review average
          halved and call it K1 (2026-07-26 review, §5.1). */}
      <td className="py-2.5 pr-3 text-right">
        <Link
          to={`/app/kpi?userId=${lawyer.id}`}
          className={`tabular-nums font-semibold hover:underline ${k1Class(lawyer.k1)}`}
        >
          {lawyer.k1 != null ? lawyer.k1.toFixed(1) : '—'}
        </Link>
      </td>
      <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">
        {lawyer.k3 != null ? lawyer.k3.toFixed(1) : '—'}
      </td>
      <td
        className={`py-2.5 text-right tabular-nums font-medium ${utilClass(lawyer.utilization)}`}
        // The hours behind the percentage, on hover (display rule D1).
        title={t('dashboard.utilBillableOfCapacity', {
          billable: (lawyer?.billableHours ?? 0).toFixed(0),
          capacity: (lawyer?.capacityHours ?? 0).toFixed(0),
        })}
      >
        {lawyer.utilization}%
      </td>
    </tr>
  );
}

/** Block 10 — Team Load table (per-lawyer Tasks / Cases / K1 / Utilization). */
export function TeamLoadTable({ period }: { period: StatisticPeriod }) {
  const { t } = useTranslation('common');
  const { data, isLoading } = useMDTeamLoad(period);
  const lawyers = data?.lawyers ?? [];

  return (
    <Card>
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users size={16} className="text-slate-400" />
          {t('dashboard.case360.teamLoadTitle')}
        </CardTitle>
        <CardDescription>
          {t('dashboard.case360.teamLoadDesc')}
          {/* Which period the row figures cover — the block used to state none
              while three blocks on the page ran on different windows (D4). */}
          {' · '}
          {t('dashboard.periodLabel', { period })}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="h-40 rounded-md bg-slate-100 animate-pulse" />
        ) : lawyers.length === 0 ? (
          <p className="text-sm text-slate-400">{t('dashboard.case360.groupEmpty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[0.75rem] font-medium uppercase tracking-wider text-slate-500">
                  <th className="py-2 pr-3 text-left font-medium">{t('dashboard.teamLoad.name')}</th>
                  <th className="py-2 pr-3 text-left font-medium">{t('dashboard.teamLoad.role')}</th>
                  <th className="py-2 pr-3 text-right font-medium">{t('dashboard.teamLoad.tasks')}</th>
                  <th className="py-2 pr-3 text-right font-medium">{t('dashboard.teamLoad.cases')}</th>
                  <th className="py-2 pr-3 text-right font-medium">{t('dashboard.teamLoad.k1')}</th>
                  <th className="py-2 pr-3 text-right font-medium">{t('dashboard.teamLoad.k3')}</th>
                  <th className="py-2 text-right font-medium">{t('dashboard.teamLoad.util')}</th>
                </tr>
              </thead>
              <tbody>
                {lawyers.map((lawyer) => (
                  <TeamLoadRow key={lawyer.id} lawyer={lawyer} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
